import { useState, useEffect } from 'react';
import { getOutOfAreaVisitors, type OutOfAreaVisitor } from '@/hooks/useLocationCheck';

export default function OutOfAreaPanel() {
  const [visitors, setVisitors] = useState<OutOfAreaVisitor[]>([]);
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    setVisitors(getOutOfAreaVisitors());
  }, []);

  const filtered = visitors.filter(v => {
    const q = search.toLowerCase();
    return !q ||
      v.name.toLowerCase().includes(q) ||
      v.whatsapp.includes(q) ||
      v.city.toLowerCase().includes(q);
  });

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const handleWhatsApp = (whatsapp: string, name: string) => {
    const msg = encodeURIComponent(
      `Namaste ${name}! 👋\n\nClothyBazar se baat kar rahe hain. Aapne hamare naye products ke baare mein jaanna chahte the.\n\nAaj ka latest collection dekhein: https://clothybazar.in\n\nKoi bhi product pasand aaye toh batao! 🛍️`
    );
    const num = whatsapp.replace(/\D/g, '');
    const fullNum = num.startsWith('91') ? num : '91' + num;
    window.open(`https://wa.me/${fullNum}?text=${msg}`, '_blank');
  };

  const handleClearAll = () => {
    if (window.confirm('Sare out-of-area visitors delete karo?')) {
      localStorage.removeItem('out_of_area_visitors');
      setVisitors([]);
    }
  };

  const formatDate = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) +
      ' · ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shrink-0">
              <i className="ri-map-pin-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-gray-900 font-bold text-base leading-none">Out of Area Visitors</h2>
              <p className="text-gray-400 text-xs mt-0.5">Jo customers aapke delivery area se bahar hain</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1.5 rounded-xl bg-orange-100 text-orange-700 font-bold text-sm">
              {visitors.length} registered
            </span>
            {visitors.length > 0 && (
              <button
                onClick={handleClearAll}
                className="px-3 py-1.5 rounded-xl bg-red-50 text-red-500 font-semibold text-xs hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-delete-bin-line mr-1"></i>Clear All
              </button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-orange-50 rounded-xl p-3 text-center border border-orange-100">
            <p className="text-orange-600 font-bold text-xl leading-none">{visitors.length}</p>
            <p className="text-orange-500 text-xs mt-1">Total Visitors</p>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100">
            <p className="text-green-600 font-bold text-xl leading-none">
              {[...new Set(visitors.map(v => v.city))].length}
            </p>
            <p className="text-green-500 text-xs mt-1">Cities</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-200">
            <p className="text-gray-700 font-bold text-xl leading-none">
              {visitors.filter(v => {
                const today = new Date().toDateString();
                return new Date(v.timestamp).toDateString() === today;
              }).length}
            </p>
            <p className="text-gray-500 text-xs mt-1">Today</p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      {visitors.length > 0 && (
        <div className="relative">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Naam, WhatsApp ya city se search karo..."
            className="w-full pl-10 pr-4 py-3 rounded-2xl border border-gray-200 bg-white text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
        </div>
      )}

      {/* Visitors List */}
      {visitors.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 flex flex-col items-center justify-center py-16 gap-4">
          <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center">
            <i className="ri-map-pin-line text-3xl text-orange-400"></i>
          </div>
          <div className="text-center">
            <p className="text-gray-700 font-semibold">Koi Out-of-Area Visitor Nahi</p>
            <p className="text-gray-400 text-sm mt-1">Jab koi customer delivery area se bahar hoga, yahan dikhega</p>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 flex flex-col items-center justify-center py-12 gap-3">
          <i className="ri-search-line text-3xl text-gray-300"></i>
          <p className="text-gray-400 text-sm">Koi match nahi mila</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((visitor) => (
            <div key={visitor.id} className="bg-white rounded-2xl border border-gray-100 p-4 hover:border-orange-200 transition-all">
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center shrink-0">
                  <span className="text-orange-600 font-bold text-base">
                    {visitor.name.charAt(0).toUpperCase()}
                  </span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div>
                      <p className="text-gray-900 font-bold text-sm leading-none">{visitor.name}</p>
                      <p className="text-gray-400 text-xs mt-0.5">{formatDate(visitor.timestamp)}</p>
                    </div>
                    <div className="flex items-center gap-1.5 bg-red-50 border border-red-100 rounded-xl px-2.5 py-1">
                      <i className="ri-map-pin-2-fill text-red-500 text-xs"></i>
                      <span className="text-red-600 font-bold text-xs">{visitor.distanceKm} km away</span>
                    </div>
                  </div>

                  {/* Location Row */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center gap-1.5 bg-gray-50 rounded-xl px-3 py-1.5 border border-gray-100 min-w-0 flex-1">
                      <i className="ri-map-2-line text-gray-400 text-sm shrink-0"></i>
                      <span className="text-gray-600 text-xs font-medium truncate">{visitor.city}</span>
                    </div>
                    <a
                      href={`https://maps.google.com/?q=${visitor.lat},${visitor.lng}`}
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="flex items-center gap-1.5 bg-gray-50 rounded-xl px-3 py-1.5 border border-gray-100 text-gray-500 hover:text-gray-900 hover:border-gray-300 transition-all cursor-pointer whitespace-nowrap text-xs font-medium"
                    >
                      <i className="ri-external-link-line text-sm"></i>
                      Map
                    </a>
                  </div>

                  {/* WhatsApp Row */}
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 bg-green-50 rounded-xl px-3 py-2 border border-green-100 flex-1 min-w-0">
                      <i className="ri-whatsapp-fill text-green-500 shrink-0"></i>
                      <span className="text-gray-800 font-semibold text-sm">+91 {visitor.whatsapp}</span>
                    </div>
                    <button
                      onClick={() => handleCopy(visitor.whatsapp, visitor.id)}
                      className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-all cursor-pointer shrink-0"
                      title="Copy number"
                    >
                      <i className={copiedId === visitor.id ? 'ri-check-line text-green-500' : 'ri-file-copy-line text-sm'}></i>
                    </button>
                    <button
                      onClick={() => handleWhatsApp(visitor.whatsapp, visitor.name)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-green-500 text-white font-bold text-xs hover:bg-green-600 transition-all cursor-pointer whitespace-nowrap shrink-0"
                    >
                      <i className="ri-send-plane-fill text-sm"></i>
                      Share Product
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filtered.length > 0 && (
        <p className="text-center text-gray-400 text-xs py-2">
          {filtered.length} of {visitors.length} visitors shown
        </p>
      )}
    </div>
  );
}
